import {
  heartFrontDefinition,
  isHeartFrontId,
  type HeartFrontId,
} from "../../../cloudflare/src/game/heart-front-definition";
import { heartFrontEntryForServerPosition } from "../../../cloudflare/src/game/heart-front-entry";
import type { GameLocation } from "../types";

type UnknownRecord = Record<string, unknown>;

function record(value: unknown): UnknownRecord | undefined {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? value as UnknownRecord
    : undefined;
}

function gameLocation(value: unknown): GameLocation | undefined {
  const source = record(value);
  if (
    !source ||
    typeof source.sectorId !== "string" ||
    !Number.isSafeInteger(source.ring) ||
    !Number.isSafeInteger(source.sector) ||
    !Number.isSafeInteger(source.q) ||
    !Number.isSafeInteger(source.r)
  ) {
    return undefined;
  }
  return {
    sectorId: source.sectorId,
    ring: source.ring as number,
    sector: source.sector as number,
    q: source.q as number,
    r: source.r as number,
  };
}

function nonEmptyStringOrNull(value: unknown): value is string | null {
  return value === null || (typeof value === "string" && value.length > 0);
}

function frontForSupplyMovement(value: unknown): HeartFrontId | undefined {
  const movement = record(value);
  const supply = record(movement?.supply);
  if (
    !movement ||
    movement.mission !== "a1_supply" ||
    !supply ||
    !isHeartFrontId(supply.frontId) ||
    typeof supply.approachSectorId !== "string" ||
    !nonEmptyStringOrNull(supply.heldAt) ||
    !nonEmptyStringOrNull(supply.recalledAt) ||
    !nonEmptyStringOrNull(movement.returnsAt)
  ) {
    return undefined;
  }

  const definition = heartFrontDefinition(supply.frontId);
  const destination = gameLocation(movement.destination);
  const entry = definition
    ? heartFrontEntryForServerPosition({
        sectorId: `S1_${definition.front - 1}`,
        ring: 1,
        sector: definition.front - 1,
        q: 0,
        r: 0,
      })
    : undefined;
  if (
    !definition ||
    !entry ||
    supply.approachSectorId !== `S1_${definition.front - 1}` ||
    !destination ||
    destination.sectorId !== entry.approachSectorId ||
    destination.ring !== 1 ||
    destination.sector !== definition.front - 1 ||
    destination.q !== entry.q ||
    destination.r !== entry.r
  ) {
    return undefined;
  }

  const phaseIsValid = movement.phase === "outbound"
    ? supply.heldAt === null && supply.recalledAt === null && movement.returnsAt === null
    : movement.phase === "holding"
      ? supply.heldAt !== null && supply.recalledAt === null && movement.returnsAt === null
      : movement.phase === "returning"
        ? supply.heldAt !== null && supply.recalledAt !== null && movement.returnsAt !== null
        : false;

  return phaseIsValid ? supply.frontId : undefined;
}

/**
 * Returns the only Heart Front proved by the authoritative player snapshot.
 * Public selection, URL state and iframe messages are deliberately absent.
 */
export function activeHeartFrontForSnapshot(snapshot: unknown): HeartFrontId | undefined {
  const envelope = record(snapshot);
  const state = record(envelope?.state);
  if (envelope?.initialized !== true || !state) return undefined;

  const locationValue = state.location;
  const location = locationValue === undefined ? undefined : gameLocation(locationValue);
  if (locationValue !== undefined && !location) return undefined;
  const positionFront = location
    ? heartFrontEntryForServerPosition(location)?.frontId
    : undefined;

  const movementsValue = state.movements;
  if (movementsValue !== undefined && !Array.isArray(movementsValue)) return undefined;
  const movements = movementsValue ?? [];
  if (movements.some((movement) => !record(movement))) return undefined;

  const supplyMovements = movements.filter((movement) =>
    record(movement)?.mission === "a1_supply" || record(movement)?.supply != null
  );
  if (supplyMovements.length === 0) return positionFront;
  if (supplyMovements.length !== 1) return undefined;

  const movementFront = frontForSupplyMovement(supplyMovements[0]);
  if (!movementFront) return undefined;
  if (positionFront && positionFront !== movementFront) return undefined;
  return movementFront;
}

function exactKeys(value: UnknownRecord, expected: readonly string[]): boolean {
  const keys = Object.keys(value).sort();
  const sorted = [...expected].sort();
  return keys.length === sorted.length && keys.every((key, index) => key === sorted[index]);
}

/**
 * Returns the private Heart Front the Shell may attempt to connect to.
 * The server repeats and owns every check; this client guard only prevents a
 * premature ticket request for a progression-locked Front.
 */
export function privateHeartFrontForSnapshot(snapshot: unknown): HeartFrontId | undefined {
  const frontId = activeHeartFrontForSnapshot(snapshot);
  if (frontId === "heart-front-1") return frontId;
  const envelope = record(snapshot);
  if (frontId === "heart-front-2") {
    const directive = record(envelope?.seneschalDirective);
    const source = record(directive?.source);
    return directive?.schema === "vaelor-seneschal-directive-public/v1" &&
      directive.status === "completed" &&
      directive.revision === 3 &&
      typeof directive.completedAt === "string" &&
      Number.isFinite(Date.parse(directive.completedAt)) &&
      source?.consequence === "front-held" &&
      source.frontNumber === 1 &&
      source.heldFrontCount === 1
      ? frontId
      : undefined;
  }
  if (frontId !== "heart-front-3" && frontId !== "heart-front-4" && frontId !== "heart-front-5" && frontId !== "heart-front-6") return undefined;

  const access = record(envelope?.privateHeartFrontAccess);
  const source = record(access?.source);
  const frontNumber = Number(frontId.slice("heart-front-".length));
  return access !== undefined && source !== undefined &&
    exactKeys(access, ["schema", "frontId", "source"]) &&
    exactKeys(source, ["heldFrontCount", "chronicleRevision"]) &&
    access.schema === "vaelor-private-heart-front-access/v1" &&
    access.frontId === frontId &&
    (source.heldFrontCount === frontNumber - 1 || source.heldFrontCount === frontNumber) &&
    Number.isSafeInteger(source.chronicleRevision) &&
    Number(source.chronicleRevision) >= 0
    ? frontId
    : undefined;
}
